import React, { useCallback } from "react";
import Grid from "@mui/material/Grid";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import FaceTwoToneIcon from "@mui/icons-material/FaceTwoTone";
import { NewPoll, NoPollsMessage } from "@Styled/Pages/Home";
import { PollItem } from "@Components";
import { IChannel, IPoll, ITextFieldCollection } from "@Interfaces";
import TextFieldCollectionCard from "@Components/TextFieldCollectionCard";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import { useEditMode } from "@Hooks/useEditMode";
import { EditModeToolbar } from "@Components/EditModeToolbar";
import StrictModeDroppable from "@Components/StrictModeDroppable";

export type PollsTabItem =
    | { type: "poll"; poll: IPoll }
    | { type: "textfield"; textField: ITextFieldCollection };

interface PollsTabProps {
    items?: PollsTabItem[];
    onCreateNewPoll: () => void;
    selectedChannel?: IChannel;
    isLoading?: boolean;
    onTextFieldEdit?: (textFieldId: string, question: string) => void;
    onTextFieldDelete?: (textFieldId: string) => void;
    textFields?: ITextFieldCollection[];
    onReorderItems?: (
        orderedIds: string[],
        previousOrder: string[]
    ) => Promise<void> | void;
    canReorder?: boolean;
    canAddPoll?: boolean;
    isChannelAdmin?: boolean;
}

export const PollsTab: React.FC<PollsTabProps> = ({
    items = [],
    onCreateNewPoll,
    selectedChannel,
    isLoading = false,
    onTextFieldEdit,
    onTextFieldDelete,
    textFields,
    onReorderItems,
    canReorder = false,
    canAddPoll = true,
    isChannelAdmin = false,
}) => {
    const { t } = useTranslation();
    const timeZero = 0;
    const newPollLabel = t("home.tabs.create_poll_button");
    const hasItems = items.length > 0;

    // Edit mode state management
    const [editModeState, editModeActions] = useEditMode<PollsTabItem>({
        initialItems: items,
        getItemId: (item) =>
            item.type === "poll"
                ? (item.poll._id as string)
                : (item.textField._id as string),
        onSave: async (orderedIds) => {
            if (!onReorderItems) return;

            const previousIds = items.map((item) =>
                item.type === "poll"
                    ? (item.poll._id as string)
                    : (item.textField._id as string)
            );

            await onReorderItems(orderedIds, previousIds);
        },
    });

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!editModeState.isEditMode) {
                return;
            }

            const { source, destination } = result;

            // If dropped outside the list or in the same position
            if (
                !destination ||
                (source.index === destination.index &&
                    source.droppableId === destination.droppableId)
            ) {
                return;
            }

            editModeActions.reorderItems(source.index, destination.index);
        },
        [editModeState.isEditMode, editModeActions]
    );

    const handleNewPollKeyDown = (
        event: React.KeyboardEvent<HTMLImageElement>
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onCreateNewPoll();
        }
    };

    const votingTimeLimit =
        (selectedChannel?.settings?.voting_time_limit as number) || timeZero;
    const hideResultSetting =
        (selectedChannel?.settings?.hide_results_before_voting as boolean) ||
        false;

    const renderItems = () => {
        const itemsToRender = editModeState.isEditMode
            ? editModeState.items
            : items;

        if (editModeState.isEditMode) {
            return (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <StrictModeDroppable droppableId="items-list" type="ITEM">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{
                                    backgroundColor: snapshot.isDraggingOver
                                        ? "rgba(0, 0, 0, 0.02)"
                                        : "transparent",
                                }}
                            >
                                {itemsToRender.map((item, index) => (
                                    <Draggable
                                        key={
                                            item.type === "poll"
                                                ? item.poll._id
                                                : item.textField._id
                                        }
                                        draggableId={
                                            item.type === "poll"
                                                ? (item.poll._id as string)
                                                : (item.textField._id as string)
                                        }
                                        index={index}
                                        isDragDisabled={editModeState.isSaving}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    opacity: snapshot.isDragging
                                                        ? 0.5
                                                        : 1,
                                                    ...provided.draggableProps
                                                        .style,
                                                }}
                                            >
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        {item.type ===
                                                        "poll" ? (
                                                            <PollItem
                                                                {...item.poll}
                                                                votingTimeLimit={
                                                                    votingTimeLimit
                                                                }
                                                                isHideResult={
                                                                    hideResultSetting
                                                                }
                                                            />
                                                        ) : (
                                                            <TextFieldCollectionCard
                                                                textField={
                                                                    item.textField
                                                                }
                                                                published={
                                                                    item
                                                                        .textField
                                                                        .createdAt
                                                                }
                                                                onEditTextField={
                                                                    onTextFieldEdit
                                                                }
                                                                onDeleteTextField={
                                                                    onTextFieldDelete
                                                                }
                                                                isChannelAdmin={
                                                                    isChannelAdmin
                                                                }
                                                            />
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </StrictModeDroppable>
                </DragDropContext>
            );
        }

        return itemsToRender.map((item) => (
            <Grid
                item
                xs={12}
                key={item.type === "poll" ? item.poll._id : item.textField._id}
            >
                {item.type === "poll" ? (
                    <PollItem
                        {...item.poll}
                        votingTimeLimit={votingTimeLimit}
                        isHideResult={hideResultSetting}
                    />
                ) : (
                    <TextFieldCollectionCard
                        textField={item.textField}
                        published={item.textField.createdAt}
                        onEditTextField={onTextFieldEdit}
                        onDeleteTextField={onTextFieldDelete}
                        isChannelAdmin={isChannelAdmin}
                    />
                )}
            </Grid>
        ));
    };

    return (
        <>
            {canAddPoll && (
                <Grid item xs={12}>
                    <NewPoll
                        src="/images/new-poll-btn.svg"
                        alt={newPollLabel}
                        role="button"
                        tabIndex={0}
                        onClick={onCreateNewPoll}
                        onKeyDown={handleNewPollKeyDown}
                        aria-label={newPollLabel}
                    />
                </Grid>
            )}

            {canReorder && hasItems && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                    <EditModeToolbar
                        isEditMode={editModeState.isEditMode}
                        isSaving={editModeState.isSaving}
                        error={editModeState.error}
                        onEnterEdit={editModeActions.enterEditMode}
                        onSave={editModeActions.saveChanges}
                        onDiscard={editModeActions.discardChanges}
                        onClearError={editModeActions.clearError}
                    />
                </Grid>
            )}

            {isLoading ? (
                <Grid container sx={{ pb: "90px" }}>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress size={32} />
                        </Box>
                    </Grid>
                </Grid>
            ) : !hasItems ? (
                <Grid container sx={{ pb: "90px" }}>
                    <Grid item xs={12}>
                        <NoPollsMessage role="status" aria-live="polite">
                            {t("home.no_polls")}
                            <FaceTwoToneIcon
                                aria-hidden="true"
                                focusable="false"
                            />
                        </NoPollsMessage>
                    </Grid>
                </Grid>
            ) : (
                <Grid container sx={{ pb: "90px" }}>
                    {renderItems()}
                </Grid>
            )}
        </>
    );
};
